import { authDelete, authGet, authPatch, authPost } from './http';

export interface AppNotification {
  id: string;
  kind: string;
  title: string;
  body: string;
  read: boolean;
  created_at: string;
}

export const listNotifications = (token: string) =>
  authGet<{ notifications: AppNotification[] }>('/notifications', token).then((r) => r.notifications);

export const markAllRead = (token: string) =>
  authPost<void>('/notifications/read-all', token);

export interface DeviceTokenPayload {
  token: string;
  platform: 'iOS' | 'Android';
  device_model?: string;
}

export const registerDeviceToken = (token: string, payload: DeviceTokenPayload) =>
  authPost<void>('/users/device-tokens', token, payload);

export const unregisterDeviceToken = (token: string, deviceToken: string) =>
  authDelete<void>(`/users/device-tokens?token=${encodeURIComponent(deviceToken)}`, token);

// --- preference centre (CRM-07 / SEC-19) ---

export type NotificationCategory = 'transactional' | 'promotional' | 'operational';
export type NotificationChannel = 'inbox' | 'push' | 'email' | 'sms' | 'whatsapp';

export interface PreferenceCell {
  category: NotificationCategory;
  channel: NotificationChannel;
  enabled: boolean;
  /** Transactional inbox: on and unchangeable. The UI renders it disabled
   *  rather than offering a switch the API will refuse. */
  locked: boolean;
  /** A deliberate choice as opposed to an inherited default. */
  explicit: boolean;
  /** The rail needs a verified opt-in before promotional mail can flow, so the
   *  UI can explain why a switch that is ON still isn't delivering. */
  requires_consent: boolean;
}

export interface NotificationConsent {
  channel: NotificationChannel;
  granted: boolean;
  source?: string;
  updated_at?: string;
}

export interface PreferenceMatrix {
  cells: PreferenceCell[];
  consents: NotificationConsent[];
  /** Rails with a wired adapter. A fan should not be offered a WhatsApp switch
   *  that cannot do anything. */
  available_channels: Record<string, boolean>;
}

export interface PreferenceUpdate {
  category: NotificationCategory;
  channel: NotificationChannel;
  enabled: boolean;
}

export const getNotificationPreferences = (token: string) =>
  authGet<PreferenceMatrix>('/me/notification-preferences', token);

/** Sparse: only the cells that changed. The server resolves the rest from the
 *  registry defaults, so the client never has to reimplement them. */
export const updateNotificationPreferences = (token: string, preferences: PreferenceUpdate[]) =>
  authPatch<PreferenceMatrix>('/me/notification-preferences', token, { preferences });
